import { showNotification } from "immigram-ui-cmon/src/components/Notification/Notification";
import toPairs from "lodash/toPairs";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useRef } from "react";

import useTransparentState from "@/lib/hook/useTransparentState";

import { ImmigramForm } from "@/components/ImmigramForm/ImmigramForm";

import { useApplicationFormConfig } from "@/core/client/application/applicationForm/config/useApplicationFormConfig";
import { useApplicationForm } from "@/core/client/application/applicationForm/useApplicationForm";
import { useApplicationFormCreateMutation } from "@/core/client/application/applicationForm/useApplicationFormCreateMutation";
import { useApplicationFormFieldMutation } from "@/core/client/application/applicationForm/useApplicationFormFieldMutation";
import { useApplicationFormValidateMutation } from "@/core/client/application/applicationForm/useApplicationFormValidateMutation";

import { ImmigramFormSkeleton } from "./ImmigramFormSkeleton";

interface ImmigramFormConnectedProps {
  applicationId: string;
  formNamespace?: string;
  formInstanceId?: string;
  formParentId?: string;
  editable?: boolean;
  onFieldChange?: (itemConfig: any, value: string) => void;
  onFormInstanceCreate?: (formInstanceId: string) => void;
  onLoadingStart?: () => void;
  onLoadingEnd?: () => void;
  onIsValidChange?: (isValid: boolean) => void;
  showValidationErrors?: boolean;
  placeholder?: React.ReactNode;
  placeholderClassName?: string;
  className?: string;
  withComments?: boolean;
  externalFormValues?: Record<string, string>;
  hiddenFieldNames?: string[];
  isO1Visa?: boolean;
}

export function ImmigramFormConnected({
  onFieldChange,
  onFormInstanceCreate,
  onLoadingStart,
  onLoadingEnd,
  onIsValidChange,
  showValidationErrors,
  editable,
  applicationId,
  formInstanceId: initialFormInstanceId,
  formNamespace,
  formParentId,
  className,
  withComments,
  externalFormValues,
  placeholder,
  placeholderClassName,
  hiddenFieldNames,
  isO1Visa,
}: ImmigramFormConnectedProps) {
  const { t } = useTranslation();

  const {
    mutateAsync: applicationFormCreate,
    data: createdForm,
    isError: isFormCreateError,
    isIdle: isFormCreateIdle,
  } = useApplicationFormCreateMutation();

  const instanceId = initialFormInstanceId || createdForm?.id;

  const { data: formData, isFetching: isFormDataLoading } = useApplicationForm(
    applicationId,
    initialFormInstanceId,
    {
      keepPreviousData: false,
      enabled: !!initialFormInstanceId,
    }
  );

  const isDisabled = !editable || isFormCreateError;

  const initialFormFieldValues = useMemo(() => {
    return (formData?.fields || []).reduce(
      (acc: Record<string, any>, field: any) => {
        acc[field.name] = field.value;
        return acc;
      },
      {
        ...(externalFormValues || {}),
      }
    );
  }, [formData, externalFormValues]);

  const [formFieldValues, setFormFieldValues] =
    useTransparentState<null | Record<string, any>>(initialFormFieldValues);

  const { config: formConfig, isFetching: isFormConfigLoading } =
    useApplicationFormConfig(
      applicationId,
      formNamespace,
      formFieldValues || {}
    );
  const processedFormConfig = useMemo(() => {
    if (!formConfig) {
      return [];
    }

    if (!isO1Visa) {
      return formConfig;
    }

    const markdownKeysMap: Record<string, string> = {
      "application:experience.employment.proof.compensationHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.employment.proof.contributionHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.employment.proof.investor.compensationHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.employment.proof.investor.contributionHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.employment.proof.investor.dealsHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.employment.proof.investor.promotionHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.employment.proof.promotionHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.entrepreneurship.proof.contributionHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.entrepreneurship.proof.essentialsFundsHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.entrepreneurship.proof.raisedFundsHelp_md":
        "o1.experience.employment.entrepreneurship.proof.",
      "application:experience.entrepreneurship.proof.essentialsFunds.option6_desc_md":
        "application:experience.entrepreneurship.o1.proof.essentialsFunds.option6_desc_md",
      "application:profJorn.companyId.field.metricsEntrepreneur.desc_md":
        "o1.profJorn.companyId.field.metricsEntrepreneur.desc_md",
    };

    const o1DescriptionKeysMap: Record<string, string> = {
      "application:profJorn.project.employment.metrics.description.howDidYouLeadTheGrowth":
        "application:profJorn.project.o1.employment.metrics.description.howDidYouLeadTheGrowth",
      "application:profJorn.project.employment.metricsInvestor.description":
        "application:profJorn.project.o1.employment.metricsInvestor.description",
      "application:profJorn.project.entrepreneurship.metrics.description.howDidYouLeadTheGrowth":
        "application:profJorn.project.o1.entrepreneurship.metrics.description.howDidYouLeadTheGrowth",
    };
    const hiddenMarkdownDescriptionKeyForO1 =
      "application:experience.entrepreneurship.proof.essentialsFunds.option7_desc_md";

    const replaceDescriptionForO1 = (description: any) => {
      if (!description) {
        return description;
      }

      if (typeof description !== "string" && typeof description !== "object") {
        return description;
      }

      const descriptionI18nKey =
        typeof description === "string"
          ? description
          : (description as any)?.i18n &&
            typeof (description as any).i18n === "string"
          ? (description as any).i18n
          : null;
      const replacementDescription =
        descriptionI18nKey && o1DescriptionKeysMap[descriptionI18nKey];

      if (replacementDescription) {
        return typeof description === "string"
          ? replacementDescription
          : { i18n: replacementDescription };
      }

      const mdKey = (description as any)?.i18n && (description as any).i18n.md;
      if (mdKey === hiddenMarkdownDescriptionKeyForO1) {
        return undefined;
      }

      const replacementKey = mdKey && markdownKeysMap[mdKey];

      if (!replacementKey) {
        return description;
      }

      return {
        i18n: {
          md: replacementKey,
        },
      };
    };

    return formConfig.map((elementConfig: any) => {
      const nextDescription = replaceDescriptionForO1(
        elementConfig?.description
      );
      let hasOptionsChanges = false;
      const nextOptions = Array.isArray(elementConfig?.options)
        ? elementConfig.options.map((option: any) => {
            const nextOptionDescription = replaceDescriptionForO1(
              option?.description
            );
            if (nextOptionDescription !== option?.description) {
              hasOptionsChanges = true;
              return { ...option, description: nextOptionDescription };
            }
            return option;
          })
        : elementConfig?.options;

      if (
        nextDescription === elementConfig?.description &&
        !hasOptionsChanges
      ) {
        return elementConfig;
      }

      return {
        ...elementConfig,
        description: nextDescription,
        options: nextOptions,
      };
    });
  }, [formConfig, isO1Visa]);

  const { mutateAsync: applicationFormFieldUpdate } =
    useApplicationFormFieldMutation();

  const handleCreateFormInstance = useCallback(() => {
    return applicationFormCreate({
      applicationId: applicationId,
      namespace: formNamespace,
      parentId: formParentId,
    });
  }, [formNamespace, applicationId, formParentId, applicationFormCreate]);

  const unsavedFormState = useRef<Record<string, string> | null>(null);

  const { mutateAsync: applicationFormSetValidState } =
    useApplicationFormValidateMutation();

  const handleIsValidChange = useCallback(
    (isValid: boolean) => {
      onIsValidChange?.(isValid);

      if (instanceId) {
        applicationFormSetValidState({
          applicationId: applicationId,
          formInstanceId: instanceId,
          isValid,
        });
      }
    },
    [onIsValidChange, applicationId, instanceId, applicationFormSetValidState]
  );

  const handleFieldChange = useCallback(
    async (itemConfig: any, value: any, formState: Record<string, string>) => {
      setFormFieldValues({
        ...formState,
        ...(externalFormValues || {}),
      });

      if (instanceId) {
        await applicationFormFieldUpdate({
          applicationId: applicationId,
          formInstanceId: instanceId,
          name: itemConfig.name,
          type: itemConfig.type,
          value,
        } as any);

        onFieldChange?.(itemConfig, value);

        return;
      }

      unsavedFormState.current = formState;

      if (isFormCreateIdle) {
        try {
          const { id: newFormInstanceId } = await handleCreateFormInstance();

          await Promise.all(
            toPairs(unsavedFormState.current || formState)
              .filter((pair) => !!pair[1])
              .map(([name, value]) => {
                const itemConfig = formConfig.find(
                  (itemConfig: any) => itemConfig.name === name
                );

                if (!itemConfig) {
                  return;
                }

                return applicationFormFieldUpdate({
                  applicationId: applicationId,
                  formInstanceId: newFormInstanceId,
                  name,
                  type: itemConfig.type,
                  value,
                } as any);
              })
          );

          onFormInstanceCreate?.(newFormInstanceId);
        } catch {
          showNotification({
            variant: "alert",
            title: t(
              "application:applicationForm.notification.failedToCreateForm.title"
            ),
          });
        }
      }
    },
    [
      formConfig,
      applicationId,
      instanceId,
      isFormCreateIdle,
      setFormFieldValues,
      handleCreateFormInstance,
      onFieldChange,
      onFormInstanceCreate,
      applicationFormFieldUpdate,
      externalFormValues,
      t,
    ]
  );

  const isLoading = isFormConfigLoading || isFormDataLoading;

  useEffect(() => {
    if (isLoading) {
      onLoadingStart?.();
    } else {
      onLoadingEnd?.();
    }
  }, [isLoading, onLoadingStart, onLoadingEnd]);

  if (isLoading) {
    return (
      <>
        {placeholder || (
          <ImmigramFormSkeleton className={placeholderClassName} />
        )}
      </>
    );
  }

  return (
    <div className={className}>
      <ImmigramForm
        disabled={isDisabled}
        immigramFormConfig={processedFormConfig}
        hiddenFieldNames={hiddenFieldNames}
        withComments={withComments}
        formData={formData}
        onFieldChange={handleFieldChange}
        onIsValidChange={handleIsValidChange}
        showValidationErrors={showValidationErrors}
      />
    </div>
  );
}
